<template>
    <!--
        In vue-js-modal 1.3.35 `clickToClose` gates BOTH the ESC key and the
        backdrop click, so disabling it leaves the button as the only valid
        close route. An accidental close would throw away what the user wrote.
    -->
    <modal
        :name="'recordatorio-' + evento_id"
        transition="pop-out"
        :adaptive="true"
        :width="800"
        height="auto"
        scrollable
        :click-to-close="false"
        @opened="onOpened"
        @closed="cleanup"
    >
        <div class="p-3 d-flex flex-column">
            <p class="text-muted small">
                {{ $t('recordatorios.venta_abierta') }} 😉
            </p>
            <div class="dropdown-divider"></div>
            <ValidationObserver tag="form" ref="observer" @submit.prevent="submit()">
                <div class="row">
                    <div class="col">
                        <FormInput
                            :label="$t('recordatorios.nombre')"
                            type="text"
                            name="nombre"
                            :id="nombreId"
                            data-cy="recordatorio-nombre"
                            rules="required"
                            v-model="recordatorio.nombre"
                        />
                    </div>
                    <div class="col">
                        <FormInput
                            :label="$t('recordatorios.correo')"
                            type="email"
                            name="correo"
                            :id="correoId"
                            data-cy="recordatorio-correo"
                            rules="required|email"
                            v-model="recordatorio.email"
                        />
                    </div>
                </div>
                <div class="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4 tw:my-3">
                    <FormCaptchaComponent
                        ref="captcha"
                        :siteKey="sitekey"
                        @success="onCaptchaVerified"
                        @expired="invalidateCaptcha"
                        @error="invalidateCaptcha"
                    />
                    <div class="custom-control custom-checkbox">
                        <ValidationProvider
                            v-slot="{ errors }"
                            :rules="{ required: { allowFalse: false } }"
                            name="notificaciones"
                        >
                            <input
                                class="custom-control-input"
                                type="checkbox"
                                :id="notificacionesId"
                                data-cy="recordatorio-notificaciones"
                                v-model="recordatorio.notificaciones"
                            />
                            <label :for="notificacionesId" class="custom-control-label">
                                {{ $t('recordatorios.notificaciones') }}
                            </label>
                            <span v-if="errors[0]" class="error invalid-feedback d-block">{{ errors[0] }}</span>
                        </ValidationProvider>
                    </div>
                </div>
            </ValidationObserver>
            <div class="d-flex ml-auto">
                <button
                    v-if="!loading"
                    :id="enviarId"
                    data-cy="recordatorio-enviar"
                    class="btn btn-primary btn-lg mr-2"
                    :disabled="!canSubmit"
                    @click.prevent="submit"
                >
                    {{ $t('recordatorios.enviar') }}
                </button>
                <Skeleton
                    v-else
                    padding="tw:p-0"
                    variant=""
                    customClass="tw:w-28 tw:h-12 mr-2"
                >
                    <template #skeleton>
                        <SkeletonItem customClass="tw:w-full" height="tw:h-12" />
                    </template>
                </Skeleton>
                <button
                    class="btn btn-lg red-content btn-danger"
                    data-cy="recordatorio-cerrar"
                    :disabled="loading"
                    @click.prevent="close"
                >
                    {{ $t('recordatorios.cerrar') }}
                </button>
            </div>
        </div>
    </modal>
</template>

<script setup>
    import { ref, computed, getCurrentInstance } from 'vue';
    import FormCaptchaComponent from '@/modules/ui/FormCaptchaComponent.vue';
    import { Skeleton, SkeletonItem } from '@/modules/front/ui/skeleton';
    import { useRecordatorioEvento } from '@/composables/front/events/useRecordatorioEvento';

    const props = defineProps({
        evento_id: {
            type: [String, Number],
            required: true
        }
    });

    const emit = defineEmits(['close']);

    const instance = getCurrentInstance();

    const captcha = ref(null);
    const observer = ref(null);

    const {
        recordatorio,
        sitekey,
        loading,
        canSubmit,
        onCaptchaVerified,
        onCaptchaInvalid,
        enviarRecordatorio,
        resetRecordatorio
    } = useRecordatorioEvento();

    const nombreId = computed(() => `recordatorio-nombre-${props.evento_id}`);
    const correoId = computed(() => `recordatorio-correo-${props.evento_id}`);
    const notificacionesId = computed(() => `recordatorio-notificaciones-${props.evento_id}`);
    const enviarId = computed(() => `recordatorio-enviar-${props.evento_id}`);

    /**
     * Turnstile tokens are single-use and can expire or hard-fail while the
     * user is still typing. Every invalidation must hand out a fresh challenge
     * or the form becomes unsubmittable. The widget's message explains WHY the
     * token was invalidated, so it is preserved: reset and feedback are
     * separate concerns.
     */
    const invalidateCaptcha = () => {
        onCaptchaInvalid();
        captcha.value?.reset({ keepFeedback: true });
    };

    /**
     * Full reset, used by the close cleanup: a clean widget and no leftover
     * failure message.
     */
    const resetCaptcha = () => {
        onCaptchaInvalid();
        captcha.value?.reset();
    };

    /**
     * Single cleanup path for every close route: the explicit button, a hide
     * driven by the parent, and (if they are ever re-enabled) ESC or backdrop.
     * Form state, validation state and the widget must go back together or the
     * next open inherits a dirty form.
     *
     * The button path and the plugin's own `closed` event both land here. The
     * reset itself is idempotent, but it calls into the widget and the
     * validator, so duplicate external effects are avoided with a guard that
     * re-arms every time the modal opens.
     */
    let cleanedUp = false;

    const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;

        observer.value?.reset();
        resetRecordatorio();
        resetCaptcha();
    };

    const onOpened = () => {
        cleanedUp = false;
    };

    const submit = async() => {
        const isValid = await observer.value.validate();

        if (!isValid) return;

        const { ok } = await enviarRecordatorio(props.evento_id);

        if (ok) {
            close();
            instance.proxy.$toast.success(instance.proxy.$t('recordatorios.exito'));
        } else {
            resetCaptcha();
            instance.proxy.$toast.error(instance.proxy.$t('recordatorios.error_envio'));
        }
    };

    const close = () => {
        cleanup();
        emit('close', 'recordatorio-' + props.evento_id);
    };
</script>
